import type { UpdateMyProfileBody } from '@hearth/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useState } from 'react';
import { Button, Card, CardBody, CardHeader, Field, Input, Textarea, useToast } from '../../../components/ui';
import { AUTH_QUERY_KEY, useAuth } from '../../../hooks/useAuth';
import { api, errorMessage, fieldErrors } from '../../../lib/api';

/** Anyone: how you appear in emails ({{salesperson_name}}, title, phone, signature) and your timezone. */
export function ProfileSection() {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: user?.name ?? '',
    title: user?.title ?? '',
    phone: user?.phone ?? '',
    timezone: user?.timezone ?? '',
    emailSignature: user?.emailSignature ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () =>
      api.patch('/api/users/me', {
        name: form.name,
        title: form.title || null,
        phone: form.phone || null,
        timezone: form.timezone || null,
        emailSignature: form.emailSignature || null,
      } satisfies UpdateMyProfileBody),
    onSuccess: async () => {
      setErrors({});
      toast.success('Profile saved');
      await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    },
    onError: (error) => {
      setErrors(fieldErrors(error));
      toast.error(errorMessage(error));
    },
  });
  const set = (key: keyof typeof form) => (event: { target: { value: string } }) => setForm({ ...form, [key]: event.target.value });

  return (
    <Card>
      <CardHeader title="My profile" description={`${user?.email ?? ''} — used in your emails as {{salesperson_name}}, {{salesperson_title}}, {{salesperson_phone}} and your signature.`} />
      <CardBody>
        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <Field label="Name" error={errors.name}>
            <Input value={form.name} onChange={set('name')} required />
          </Field>
          <Field label="Title" error={errors.title}>
            <Input value={form.title} onChange={set('title')} placeholder="Account executive" />
          </Field>
          <Field label="Phone" error={errors.phone}>
            <Input value={form.phone} onChange={set('phone')} placeholder="+1 514-555-0142" />
          </Field>
          <Field label="Timezone" hint="Empty = company timezone, e.g. America/Toronto" error={errors.timezone}>
            <Input value={form.timezone} onChange={set('timezone')} />
          </Field>
          <Field label="Email signature" className="sm:col-span-2" error={errors.emailSignature}>
            <Textarea rows={4} value={form.emailSignature} onChange={set('emailSignature')} />
          </Field>
          <div className="sm:col-span-2">
            <Button type="submit" variant="primary" icon={Save} loading={save.isPending}>
              Save profile
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
